import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/react-hooks';

import { fetchJSON } from '../../../../helpers/fetch';
import getAgeQuotaCount from './getAgeQuotaCount';
import processHistoryAction from './processHistoryAction';

import FlowChart from '../SurveyBuilder/components/FlowNavigation/FlowChart';
import FlowResultsChart from './FlowResultsChart';

import styles from './SurveyHistoryInspector.module.css';

import { GET_SURVEY_HISTORY } from '../../../../graphql/Survey';

export default ({
  match: {
    params: { id: surveyId }
  }
}) => {
  const [surveyData, setSurveyData] = useState([]);
  const [showActionStepIndex, setShowActionStepIndex] = useState(0);
  const [resultObject, setResultObject] = useState(null);
  const [popup, setPopup] = useState(null);
  const [quotaCounts, setQuotaCounts] = useState(null);
  const [quotaDistribution, setQuotaDistribution] = useState(null);

  const [
    resultStatsPerSurveyVersionShort,
    setResultStatsPerSurveyVersionShort
  ] = useState(null);
  const [
    resultStatsPerSurveyVersion,
    setResultStatsPerSurveyVersion
  ] = useState(null);

  const [
    resultStatsPerBlockResultsShort,
    setResultStatsPerBlockResultsShort
  ] = useState(null);
  const [resultStatsPerBlockResults, setResultStatsPerBlockResults] = useState(
    null
  );

  const [
    resultStatsPerQuestionShort,
    setResultStatsPerQuestionShort
  ] = useState(null);
  const [resultStatsPerQuestion, setResultStatsPerQuestion] = useState(null);

  const [timelineNodes, setTimelineNodes] = useState([]);

  const flowResultsChartParentRef = useRef();

  const { data: { survey: { survey } = {} } = {} } = useQuery(
    GET_SURVEY_HISTORY,
    {
      variables: {
        id: surveyId
      },
      fetchPolicy: 'network-only'
    }
  );

  const loadSurveyResults = async () => {
    const results = await fetchJSON(
      `http://localhost:8080/api/clients/v2/campaigns/${surveyId}/results`,
      localStorage.getItem('token')
    );

    if (!results || !results.json || !results.json.data) return;

    // Results per Survey Version
    const calculatedResultsStats = results.json.data.reduce((acc, r) => {
      if (
        r.attributes.surveyVersion &&
        (r.attributes.surveyVersion || r.attributes.surveyVersion === 0)
      ) {
        return {
          ...acc,
          [r.attributes.surveyVersion]: {
            count: acc[r.attributes.surveyVersion]
              ? acc[r.attributes.surveyVersion].count + 1
              : 1,
            ids: [
              ...(acc[r.attributes.surveyVersion]
                ? acc[r.attributes.surveyVersion].ids
                : []),
              r.attributes.id
            ]
          }
        };
      }
      return acc;
    }, {});
    setResultStatsPerSurveyVersion(calculatedResultsStats);

    const calculatedShortResultsStats = results.json.data.reduce((acc, r) => {
      if (
        r.attributes.surveyVersion &&
        (r.attributes.surveyVersion || r.attributes.surveyVersion === 0)
      ) {
        return {
          ...acc,
          [r.attributes.surveyVersion]: acc[r.attributes.surveyVersion]
            ? acc[r.attributes.surveyVersion] + 1
            : 1
        };
      }
      return acc;
    }, {});
    setResultStatsPerSurveyVersionShort(calculatedShortResultsStats);

    // Results per number of block_results
    const calculatedResultsBlockResultsStats = results.json.data.reduce(
      (acc, r) => {
        if (r.attributes.block_results && r.attributes.block_results.length) {
          const key = r.attributes.block_results.length;

          return {
            ...acc,
            [key]: {
              count: acc[key] ? acc[key].count + 1 : 1,
              ids: [...(acc[key] ? acc[key].ids : []), r.attributes.id]
            }
          };
        }
        return acc;
      },
      {}
    );
    setResultStatsPerBlockResults(calculatedResultsBlockResultsStats);

    const calculatedShortResultsBlockResultsStats = results.json.data.reduce(
      (acc, r) => {
        const key = r.attributes.block_results.length;

        if (r.attributes.block_results && r.attributes.block_results.length) {
          return {
            ...acc,
            [key]: acc[key] ? acc[key] + 1 : 1
          };
        }
        return acc;
      },
      {}
    );
    setResultStatsPerBlockResultsShort(calculatedShortResultsBlockResultsStats);

    // Results per question
    const calculatedResultsQuestionStats = results.json.data.reduce(
      (acc, r) => {
        if (r.attributes.block_results && r.attributes.block_results.length) {
          const newAcc = { ...acc };

          r.attributes.block_results.forEach(br => {
            if (newAcc[br.block]) {
              newAcc[br.block].count += 1;
              newAcc[br.block].ids.push(r.attributes.id);
            } else {
              newAcc[br.block] = {
                count: 1,
                ids: [r.attributes.id]
              };
            }
          });

          return newAcc;
        }
        return acc;
      },
      {}
    );
    setResultStatsPerQuestion(calculatedResultsQuestionStats);

    const calculatedShortResultsQuestionStats = results.json.data.reduce(
      (acc, r) => {
        if (r.attributes.block_results && r.attributes.block_results.length) {
          const newAcc = { ...acc };

          r.attributes.block_results.forEach(br => {
            if (newAcc[br.block]) {
              newAcc[br.block] += 1;
            } else {
              newAcc[br.block] = 1;
            }
          });

          return newAcc;
        }
        return acc;
      },
      {}
    );
    setResultStatsPerQuestionShort(calculatedShortResultsQuestionStats);

    // History + answers timeline
    const timelineNodes1 = survey.allHistory.map((h, i) => ({
      label: `History step: ${i + 1}`,
      timestamp: new Date(h.createdAt),
      timestampString: h.createdAt,
      type: 'HISTORY'
    }));
    const timelineNodes2 = results.json.data.map(r => ({
      label: `Result answer id: ${r.attributes.id}`,
      timestamp: new Date(r.attributes.createdAt),
      timestampString: r.attributes.createdAt,
      type: 'ANSWER'
    }));
    const joinedTimelineNodes = [...timelineNodes1, ...timelineNodes2];
    const sortedTimelineNodes = joinedTimelineNodes.sort(
      (a, b) => a.timestamp - b.timestamp
    );
    setTimelineNodes(sortedTimelineNodes);
  };

  useEffect(
    () => {
      if (survey) {
        const allSurveyData = [];

        survey.allHistory.forEach(h => {
          const newHistoryStep = processHistoryAction(
            h,
            allSurveyData,
            surveyId
          );
          allSurveyData.push(newHistoryStep);
        });

        setSurveyData(allSurveyData);

        // Load survey results
        loadSurveyResults();
      }
    },
    [survey]
  );

  useEffect(
    () => {
      if (survey && survey.userFilter) {
        const parsedQuotaCounts = JSON.parse(quotaCounts);
        const surveyFilters = JSON.parse(survey.userFilter);

        const genderRanges = surveyFilters.$and.filter(sF => sF.gender);

        // Theory max per gender
        const genderMaxInTheory = {};
        genderRanges.forEach(gender => {
          gender.gender.$in.forEach(g => {
            genderMaxInTheory[g] = {};
            if (gender && gender.$dynamic) {
              genderMaxInTheory[g].maxType = gender.$dynamic.$type;
              genderMaxInTheory[g].maxValue = gender.$dynamic.$max;

              if (gender.$dynamic.$type === '%') {
                genderMaxInTheory[g].maxInTheory =
                  (gender.$dynamic.$max / 100) * survey.maxResponses;
              }
              if (gender.$dynamic.$type === '#') {
                genderMaxInTheory[g].maxInTheory = gender.$dynamic.$max;
              }

              if (
                parsedQuotaCounts &&
                parsedQuotaCounts.gender &&
                parsedQuotaCounts.gender[g]
              ) {
                genderMaxInTheory[g].count = parsedQuotaCounts.gender[g].count;

                genderMaxInTheory[g].remainingPlaces =
                  genderMaxInTheory[g].maxInTheory -
                    genderMaxInTheory[g].count >
                  1;
              }
            }
          });
        });

        const ageRanges = surveyFilters.$and.filter(sF => sF.age);
        ageRanges.forEach(ageRange => {
          const crossedQuotaCounts = getAgeQuotaCount(
            parsedQuotaCounts,
            ageRange,
            genderMaxInTheory,
            survey
          );
          const newAgeRange = ageRange;
          newAgeRange.quotaCounts = crossedQuotaCounts;
        });

        setQuotaDistribution({ ageRanges, genderMaxInTheory });
      }
    },
    [quotaCounts]
  );

  if (!survey) return <div>No Survey</div>;
  if (!surveyData || surveyData.length === 0)
    return <div>Preparing survey data</div>;

  return (
    <div style={{ padding: '50px' }}>
      {popup && (
        <div className={styles.popup}>
          <div
            className={styles.close}
            role="presentation"
            onClick={() => setPopup(null)}
          >
            CLOSE
          </div>
          <pre>{JSON.stringify(JSON.parse(popup), null, 2)}</pre>
        </div>
      )}
      <div className={styles.historyStepsContainer}>
        <div className={styles.step}>
          <div className={styles.surveyNumber}>Survey version</div>
          <div />
          <div className={styles.client} />
          <div className={styles.mutation} />
        </div>
        {survey.allHistory.map((h, i) => (
          <div
            className={styles.step}
            key={`history-step-${i.toString()}`}
            onClick={() => setShowActionStepIndex(i)}
            role="presentation"
          >
            <div className={styles.surveyNumber}>{i + 1}</div>
            <div
              className={
                showActionStepIndex === i
                  ? `${styles.activate} ${styles.active}`
                  : styles.activate
              }
            >
              ACTIVATE
            </div>
            <div className={styles.client}>{h.client && h.client.email}</div>
            <div className={styles.client}>{h.createdAt}</div>
            <div
              className={styles.mutation}
              role="presentation"
              onClick={() => setPopup(h.arguments)}
            >
              {h.mutation}
            </div>
          </div>
        ))}
      </div>
      {surveyData[showActionStepIndex].content &&
        Object.values(surveyData[showActionStepIndex].surveyLists).length && (
          <div className={styles.flowChartContainer}>
            <FlowChart
              width="1200"
              height="600"
              onQuestionClick={() => {}}
              surveyLists={Object.values(
                surveyData[showActionStepIndex].surveyLists
              )}
              content={surveyData[showActionStepIndex].content}
              questions={surveyData[showActionStepIndex].questionsMapping}
              activeFlowId={null}
              showQuestionIds
            />
            <div className={styles.controlsContainer}>
              {showActionStepIndex > 0 ? (
                <div
                  className={styles.control}
                  onClick={() =>
                    setShowActionStepIndex(showActionStepIndex - 1)
                  }
                  role="presentation"
                >{`< LEFT`}</div>
              ) : (
                <div />
              )}
              {showActionStepIndex < surveyData.length - 1 ? (
                <div
                  className={styles.control}
                  onClick={() =>
                    setShowActionStepIndex(showActionStepIndex + 1)
                  }
                  role="presentation"
                >{`RIGHT >`}</div>
              ) : (
                <div />
              )}
            </div>
          </div>
        )}

      <h2>Questions Mapping</h2>
      <div className={styles.questionsContainer}>
        <pre>
          {JSON.stringify(
            surveyData[showActionStepIndex].questionsMapping,
            null,
            2
          )}
        </pre>
      </div>

      <h2>Quotas active in the Questions</h2>
      <div className={styles.questionsContainer}>
        <pre>
          {JSON.stringify(
            surveyData[showActionStepIndex].questionsWithQuotas,
            null,
            2
          )}
        </pre>
      </div>

      <h2>Content</h2>
      <div className={styles.questionsContainer}>
        <pre>
          {JSON.stringify(surveyData[showActionStepIndex].content, null, 2)}
        </pre>
      </div>

      <h2>Survey properties</h2>
      <div className={styles.questionsContainer}>
        <pre>
          {JSON.stringify(
            surveyData[showActionStepIndex].surveyProperties,
            null,
            2
          )}
        </pre>
      </div>

      <h2>History and Survey answer timeline</h2>
      <div className={styles.historyStepsContainer}>
        {timelineNodes.map((t, i) => (
          <div className={styles.step} key={`timeline-step-${i.toString()}`}>
            <div
              style={{
                backgroundColor: t.type === 'HISTORY' ? '#ffc6c2' : '#e1ffd4'
              }}
            >
              {t.label}
            </div>
            <div>{t.timestampString}</div>
          </div>
        ))}
      </div>

      <h2>Results per survey version mapping (key: survey version)</h2>
      <div className={styles.questionsContainer}>
        {resultStatsPerSurveyVersionShort ? (
          <pre>{JSON.stringify(resultStatsPerSurveyVersionShort, null, 2)}</pre>
        ) : (
          'Loading Results'
        )}
      </div>
      <div className={styles.questionsContainer}>
        {resultStatsPerSurveyVersion ? (
          <pre>{JSON.stringify(resultStatsPerSurveyVersion, null, 2)}</pre>
        ) : (
          'Loading Results'
        )}
      </div>

      <h2>
        Number of block_results mapping (key: number of block_results in result
        document)
      </h2>
      <div className={styles.questionsContainer}>
        {resultStatsPerBlockResultsShort ? (
          <pre>{JSON.stringify(resultStatsPerBlockResultsShort, null, 2)}</pre>
        ) : (
          'Loading Results'
        )}
      </div>
      <div className={styles.questionsContainer}>
        {resultStatsPerBlockResults ? (
          <pre>{JSON.stringify(resultStatsPerBlockResults, null, 2)}</pre>
        ) : (
          'Loading Results'
        )}
      </div>

      <h2>Number of Blockresults per question (key: questionId)</h2>
      <div className={styles.questionsContainer}>
        {resultStatsPerQuestionShort ? (
          <pre>{JSON.stringify(resultStatsPerQuestionShort, null, 2)}</pre>
        ) : (
          'Loading Results'
        )}
      </div>
      <div className={styles.questionsContainer}>
        {resultStatsPerQuestion ? (
          <pre>{JSON.stringify(resultStatsPerQuestion, null, 2)}</pre>
        ) : (
          'Loading Results'
        )}
      </div>

      <h2>Results object parser (results from same survey)</h2>
      <div className={styles.resultContainer}>
        <textarea onChange={e => setResultObject(e.target.value)} />
        <div ref={flowResultsChartParentRef}>
          <FlowResultsChart
            parent={flowResultsChartParentRef}
            resultObject={resultObject}
            surveyLists={Object.values(
              surveyData[showActionStepIndex].surveyLists
            )}
            height={800}
          />
        </div>
      </div>

      <h2>Quota Distribution over survey filter ranges</h2>
      <div className={styles.resultContainer}>
        <textarea onChange={e => setQuotaCounts(e.target.value)} />
      </div>
      <div className={styles.questionsContainer}>
        <pre>{JSON.stringify({ quotaDistribution }, null, 2)}</pre>
      </div>
    </div>
  );
};
